import type {
  B2cBoardView,
  IterationView,
  StageView,
  WorkstreamView,
} from "@/application/use-cases/b2c/build-b2c-board";
import type {
  B2cTaskKind,
  B2cTaskPriority,
  B2cTaskRow,
  B2cTaskStatus,
  IterationStatus,
} from "@/infrastructure/supabase/b2c-board.types";

/** The B2C board's toolbar filters — the MVP board's set, adapted to stages and marathons. */
export interface B2cBoardFilters {
  search: string;
  status: B2cTaskStatus | "all";
  stage: string | "all";
  /** The workstream's current iteration status. */
  iteration: IterationStatus | "all";
  priority: B2cTaskPriority | "all";
  kind: B2cTaskKind | "all";
  e2eOnly: boolean;
}

export const EMPTY_B2C_FILTERS: B2cBoardFilters = {
  search: "",
  status: "all",
  stage: "all",
  iteration: "all",
  priority: "all",
  kind: "all",
  e2eOnly: false,
};

/** True when any filter narrows the tasks shown (not just stages or workstreams). */
export function filtersTasks(f: B2cBoardFilters): boolean {
  return (
    f.search.trim() !== "" ||
    f.status !== "all" ||
    f.priority !== "all" ||
    f.kind !== "all" ||
    f.e2eOnly
  );
}

export function isFiltering(f: B2cBoardFilters): boolean {
  return filtersTasks(f) || f.stage !== "all" || f.iteration !== "all";
}

/**
 * Narrow the board to what the filters match. Progress figures are left as they
 * are — a filter changes what is listed, not how far a marathon has got. When a
 * filter narrows the tasks, a workstream with no matching task drops out; the
 * search also matches the workstream's own code and title, which keeps all of
 * its tasks.
 */
export function filterB2cBoard(board: B2cBoardView, f: B2cBoardFilters): B2cBoardView {
  if (!isFiltering(f)) return board;
  const q = f.search.trim().toLowerCase();
  const narrowTasks = filtersTasks(f);

  const taskMatches = (t: B2cTaskRow, wsHit: boolean) => {
    if (f.status !== "all" && t.status !== f.status) return false;
    if (f.priority !== "all" && t.priority !== f.priority) return false;
    if (f.kind !== "all" && t.kind !== f.kind) return false;
    if (f.e2eOnly && !t.is_e2e_proof) return false;
    if (q && !wsHit) {
      const hay = `${t.code} ${t.title} ${t.description ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const workstreamOf = (v: WorkstreamView): WorkstreamView | null => {
    if (f.iteration !== "all" && v.current?.iteration.status !== f.iteration) return null;
    if (!narrowTasks) return v;
    const wsHit =
      q !== "" && `${v.workstream.code} ${v.workstream.title}`.toLowerCase().includes(q);
    const iterations: IterationView[] = v.iterations.map((iv) => ({
      ...iv,
      tasks: iv.tasks.filter((t) => taskMatches(t, wsHit)),
    }));
    const unplanned = v.unplanned.filter((t) => taskMatches(t, wsHit));
    const any = iterations.some((iv) => iv.tasks.length > 0) || unplanned.length > 0;
    if (!any) return null;
    const current = v.current
      ? (iterations.find((iv) => iv.iteration.id === v.current?.iteration.id) ?? null)
      : null;
    return { ...v, iterations, unplanned, current };
  };

  const keep = (views: WorkstreamView[]) =>
    views.map(workstreamOf).filter((v): v is WorkstreamView => v !== null);

  const stages: StageView[] = board.stages
    .filter((s) => f.stage === "all" || s.stage.key === f.stage)
    .map((s) => ({ ...s, workstreams: keep(s.workstreams) }))
    .filter((s) => s.workstreams.length > 0);

  return {
    ...board,
    stages,
    unstaged: f.stage === "all" ? keep(board.unstaged) : [],
  };
}
